import React, { memo, ReactNode } from "react";
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Text } from "@UI/Components/Text/Text";
import { Icon } from "@UI/Components/Icon/Icon";
import { Card } from "@UI/Components/Card/Card";
import { CircleQuestion } from "@UI/Components/Icons/CircleQuestion";
import { CircleCheck } from "@UI/Components/Icons/CircleCheck";
import { CircleXmark } from "@UI/Components/Icons/CircleXmark";
import { Clock } from "@UI/Components/Icons/Clock";

type TMetricCardState = "reached" | "not-reached" | "rejected" | "pending" | "info";

interface IMetricCard {
  title: ReactNode;
  description?: ReactNode;
  state: TMetricCardState;
  onClick?: () => void;
  /** When provided, renders the value as a large "XX%" label inline with a ❓ icon.
   *  The header state indicator is suppressed — the ❓ lives next to the value instead. */
  percentage?: number;
  footer?: ReactNode;
  children?: ReactNode;
}

export const MetricCard = memo(
  ({
    title,
    description,
    state,
    onClick,
    percentage,
    footer,
    children,
  }: IMetricCard) => {
    const hasPercentage = percentage !== undefined;

    return (
      <Card
        padding="1rem"
        direction="column"
        height="full"
        overflow="visible"
        onClick={onClick}
        cursor={onClick ? "pointer" : undefined}
        _hover={onClick ? { shadow: "lg" } : undefined}
        footer={footer}
      >
        <Box pr={hasPercentage ? "9rem" : "2.5rem"}>
          <Text fontWeight="700" fontSize="md" color="black">
            {title}
          </Text>
          {description && (
            <Text fontSize="sm" color="gray.500">
              {description}
            </Text>
          )}
          {children && <Box mt={2}>{children}</Box>}
        </Box>

        {/* Indicator: absolutely centered in the card so it doesn't affect content width */}
        <Flex
          position="absolute"
          right="1rem"
          top="50%"
          transform="translateY(-50%)"
          align="center"
          gap={4}
          flexShrink={0}
        >
          {hasPercentage ? (
            <>
              <Text fontFamily="heading" fontSize="4xl" lineHeight="none" color="black">
                {Math.round(percentage as number)}%
              </Text>
              {state === "reached" ? (
                <Icon as={CircleCheck} color="green.500" boxSize="24px" />
              ) : (
                <Icon as={CircleQuestion} color="#666666" boxSize="24px" />
              )}
            </>
          ) : state === "reached" ? (
            <Icon as={CircleCheck} color="green.500" boxSize="24px" />
          ) : state === "rejected" ? (
            <Icon as={CircleXmark} color="red.500" boxSize="24px" />
          ) : state === "pending" ? (
            <Icon as={Clock} color="orange.500" boxSize="24px" />
          ) : (
            <Icon as={CircleQuestion} color="#666666" boxSize="24px" />
          )}
        </Flex>
      </Card>
    );
  }
);

MetricCard.displayName = "MetricCard";
